// Room page for hosting a game

import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, InfoIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import MusicDisplay from '@/components/media/MusicDisplay';
import { GameStatsRow } from '@/components/games';
import {
  TransitionMessage,
  ROOM_LIFECYCLE,
} from '../components/transitionMessage';
import RoomLobbyPanel from '@/components/room/RoomLobbyPanel';
import GameInProgressPanel from '@/components/room/GameInProgressPanel';
import Poppy from '../components/avatars/Poppy';
import AvatarGrid from '../components/avatars/AvatarGrid';
import { useAnimalStyleAvatars } from '../components/avatars/hooks';
import {
  useMedia,
  useRoomStateSelector,
  useRoomConfig,
  useRoomPlayers,
  useRoute,
  useAuth,
  useRoomState,
  useQrCode,
  useRoomGameExit,
  useRoomGameStart,
  useAnimalJoinSounds,
} from '../hooks';
import { analyticsService } from '../services/analyticsService';
import type { Emotion } from '@airhead-games/shared/configs';
import { gameLibrary } from '@airhead-games/shared/configs';
import { createLogger } from '@airhead-games/shared/utilities';
import { getMinPlayersReached } from '@airhead-games/shared/engine/roomState';

const logger = createLogger('Room');

const Room: React.FC = () => {
  const navigate = useNavigate();

  const { roomCode: urlRoomCode } = useRoute();
  const {
    roomCode: stateRoomCode,
    status,
    host,
    gameName,
    gameId,
  } = useRoomConfig();
  const roomCode = stateRoomCode || urlRoomCode;

  const { user } = useAuth();
  const { isReplayingEvents } = useRoomState();
  const isAnimalStyleRoom = useAnimalStyleAvatars();

  const { players, playerCount } = useRoomPlayers();
  const { exitToLibrary } = useRoomGameExit({
    linkLocation: 'room_page',
  });

  const notEnoughPlayers = useRoomStateSelector((state) =>
    state ? !getMinPlayersReached(state) : true
  );

  const { flushMusicManager } = useMedia();

  const playersContainerRef = useRef<HTMLDivElement>(null);
  const [globalEmotionOverride, setGlobalEmotionOverride] =
    useState<Emotion | null>(null);

  const qrCodeUrlSuffix = roomCode ? `/join?${roomCode}` : undefined;
  const qrCode = useQrCode(qrCodeUrlSuffix);
  const currentGame = useMemo(
    () => gameLibrary.find((game) => game.gameCore.gameId === gameId),
    [gameId]
  );

  const goToGame = useCallback(() => {
    analyticsService.trackButtonClick('Go To Game', {
      link_location: 'room_page',
      room_code: roomCode || 'unknown',
      game_status: status,
    });
    if (roomCode) {
      navigate(`/game/${roomCode}`);
    }
  }, [navigate, roomCode, status]);

  const transitionToGame = useCallback(async () => {
    try {
      flushMusicManager();
      goToGame();
    } catch (error) {
      logger.error('Error during game transition:', error);
    }
  }, [flushMusicManager, goToGame]);

  const { startGame, gameStarting, startGameError } = useRoomGameStart({
    roomCode,
    host,
    gameId,
    gameName,
    playerCount,
    onTransition: transitionToGame,
  });

  useEffect(() => {
    if (gameStarting) {
      setGlobalEmotionOverride(null);
    }
  }, [gameStarting]);

  const loonCost = currentGame?.gameCore.loonCost ?? 0;
  const insufficientLoons = loonCost > 0 && (user?.loonBalance ?? 0) < loonCost;

  const buttonLabel =
    status === 'playing'
      ? 'Game in progress'
      : notEnoughPlayers
        ? 'Waiting for players'
        : insufficientLoons
          ? `Insufficient Loons`
          : 'Start game';

  const isButtonDisabled =
    status === 'playing' ||
    notEnoughPlayers ||
    gameStarting ||
    insufficientLoons;

  const gameInProgress = status === 'playing' && !gameStarting;

  useAnimalJoinSounds({ players, isAnimalStyleRoom, isReplayingEvents });

  if (roomCode && !status) {
    return (
      <div className="relative h-[100svh] w-full">
        <TransitionMessage
          messages={ROOM_LIFECYCLE.startingRoom}
          showSpinner
          enableFunnyMessages
        />
      </div>
    );
  }

  return (
    <div className="relative h-[100svh] w-full overflow-hidden overscroll-none">
      <header className="fixed top-6 right-24 left-6 z-10 flex min-h-11 min-w-0 items-center gap-2 sm:right-32">
        {!gameStarting ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0"
            aria-label="Back to game library"
            title="Back to game library"
            onClick={() => void exitToLibrary()}
          >
            <ArrowLeft className="size-5" />
          </Button>
        ) : null}
        <h1 className="m-0 min-w-0 truncate pb-1 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
          {gameName || 'Unknown Game'}
        </h1>
        {currentGame ? (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0"
                aria-label={`Game details for ${currentGame.gameCore.gameName}`}
              >
                <InfoIcon className="size-5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-80 gap-3 p-3">
              <GameStatsRow
                gamePlayersMin={currentGame.gameCore.gamePlayersMin}
                gamePlayersMax={currentGame.gameCore.gamePlayersMax}
                averageDuration={currentGame.gameWiki.averageDuration}
                loonCost={currentGame.gameCore.loonCost}
              />
              <p className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
                {currentGame.gameCore.gameDescription}
              </p>
            </PopoverContent>
          </Popover>
        ) : null}
      </header>

      <main className="flex h-full w-full items-center justify-between gap-10 px-6">
        {gameInProgress ? (
          <div className="flex h-full w-full flex-1 items-center justify-center">
            <GameInProgressPanel onGoToGame={goToGame} />
          </div>
        ) : (
          <>
            <div className="w-80 shrink-0">
              <RoomLobbyPanel
                roomCode={roomCode || ''}
                qrCode={qrCode}
                onStartGame={startGame}
                buttonLabel={buttonLabel}
                disabled={isButtonDisabled}
                starting={gameStarting}
                onStartButtonHoverChange={(hovered) =>
                  setGlobalEmotionOverride(hovered ? 'surprised' : null)
                }
                error={startGameError}
              />
            </div>
            <div
              className="flex h-[640px] min-w-0 flex-1 items-center justify-center"
              ref={playersContainerRef}
            >
              <AvatarGrid
                players={players}
                containerRef={playersContainerRef}
                showBottomLabel={(player) => player.playerName}
                minSize={80}
                maxSize={480}
                rowGap={0}
                columnGap={0}
                layoutType="grid"
                enableFloatEntrance={true}
                emotionOverride={globalEmotionOverride}
                avatarProps={{
                  enableFloat: true,
                  enableBlinking: true,
                  enableMouthMovement: true,
                  animalStyle: isAnimalStyleRoom,
                }}
              />
            </div>
          </>
        )}

        <div className="fixed right-0 top-0 z-[2000]">
          <Poppy size={240} />
        </div>
        <MusicDisplay />
      </main>
    </div>
  );
};

export default Room;
